import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import {
  BackLink,
  Container,
  Heading,
  Muted,
  ProgressBar,
  Screen,
  Stack,
  Text,
} from '@fluent/ui';
import { useAuth } from '../auth/authStore.js';
import { useProgressSnapshot } from '../achievements/useSnapshot.js';
import { useMissions } from '../missions/hooks.js';
import { MissionsSections } from '../missions/MissionsSections.js';
import {
  ACHIEVEMENTS,
  ACHIEVEMENT_CATEGORIES,
  achievementDescriptionKey,
  achievementTitleKey,
  isUnlocked,
  progressOf,
  type AchievementDefinition,
  type ProgressSnapshot,
} from '../achievements/definitions.js';
import { AppBottomNav } from '../nav/AppBottomNav.js';
import { EVERYTHING_FREE } from '../subscription/freeMode.js';
import './Achievements.css';

/** "Become Pro" cannot be earned while everything is free (freeMode.ts). */
const SHOWN_ACHIEVEMENTS = EVERYTHING_FREE
  ? ACHIEVEMENTS.filter((a) => a.id !== 'pro_member')
  : ACHIEVEMENTS;

/**
 * Awards — the trophy case (ჯილდოები tab). Gold identity, bold-pass
 * language. Badges are grouped into tiered category ladders; unlock state
 * is computed client-side from existing progress hooks (no new tables),
 * so adding a badge is one definition + two i18n strings per locale.
 *
 * <AwardsView> is presentational (pure snapshot in) so the /awards-review
 * design-QA route can mount it without a Supabase session.
 */
/**
 * The MISSIONS & CHALLENGES hub (Tornike, 2026-07-17): coin balance + the
 * 28-mission catalog on top, the one-shot award badges below as long-term
 * challenges.
 *
 * Since 2026-10-18 it has no tab of its own (ფრაზები took the slot): it opens
 * from the Home missions card, the Profile coins row and /missions. The bar
 * stays (no tab active) and a back link returns to where the learner came
 * from — or Home on a cold deep link, where there is no in-app history.
 */
export function Achievements() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const snapshot = useProgressSnapshot();
  const missions = useMissions();

  if (!user) return null;

  const goBack = () => (hasInAppHistory() ? navigate(-1) : navigate('/'));

  return (
    <Screen>
      <Container size="sm">
        <Stack gap={6} style={{ padding: 'var(--space-4, 16px) 0 0' }}>
          <Stack gap={2}>
            <BackLink label={t('missions.back')} onPress={goBack} />
            <Heading level={1}>{t('missions.title')}</Heading>
            <Muted>{t('missions.subtitle')}</Muted>
          </Stack>
          <MissionsSections
            state={missions.data ?? null}
            loading={missions.isLoading && !missions.data}
            failed={missions.isError && !missions.data}
            onRetry={() => void missions.refetch()}
          />
        </Stack>
      </Container>
      <AwardsView snapshot={snapshot} />
      <AppBottomNav />
    </Screen>
  );
}

/** True when the browser's previous entry is a page of this app. react-router
 *  numbers its entries (`history.state.idx`); 0 is the first page of the
 *  visit — a cold deep link, where -1 would leave the app (/missions → its
 *  <Navigate replace> also stays at 0, so `location.key` can't tell). */
function hasInAppHistory(): boolean {
  const state: unknown = window.history.state;
  return (
    typeof state === 'object' &&
    state !== null &&
    'idx' in state &&
    typeof state.idx === 'number' &&
    state.idx > 0
  );
}

export function AwardsView({ snapshot }: { snapshot: ProgressSnapshot }) {
  const { t } = useTranslation();
  const unlockedCount = SHOWN_ACHIEVEMENTS.filter((a) => isUnlocked(a, snapshot)).length;
  const nextUp = pickNextUp(snapshot);

  return (
    <Container size="sm">
      <Stack
        gap={6}
        style={{
          padding:
            'var(--space-6, 24px) 0 calc(var(--space-16, 64px) + 88px + env(safe-area-inset-bottom))',
        }}
      >
        <Stack gap={2}>
          <Heading level={2}>{t('missions.challenges')}</Heading>
          <Muted>{t('achievements.subtitle')}</Muted>
        </Stack>

        <section className="fluent-awards__hero" aria-label={t('achievements.title')}>
          <div className="fluent-awards__trophy" aria-hidden="true">
            🏆
          </div>
          <Stack gap={1} align="center" style={{ textAlign: 'center' }}>
            <div className="fluent-awards__count">
              {unlockedCount}
              <span className="fluent-awards__count-total"> / {SHOWN_ACHIEVEMENTS.length}</span>
            </div>
            <Text tone="muted" size="sm">
              {t('achievements.earned_label')}
            </Text>
          </Stack>
          <ProgressBar
            value={unlockedCount / SHOWN_ACHIEVEMENTS.length}
            tone="xp"
            size="lg"
            label={t('achievements.summary', {
              unlocked: unlockedCount,
              total: SHOWN_ACHIEVEMENTS.length,
            })}
            style={{ alignSelf: 'stretch' }}
          />
          {nextUp && (
            <div className="fluent-awards__next">
              <span className="fluent-awards__next-icon" aria-hidden="true">
                {nextUp.icon}
              </span>
              <Text size="sm">
                <Text as="span" size="sm" tone="muted">
                  {t('achievements.next_up')}{' '}
                </Text>
                <Text as="span" size="sm" weight="bold">
                  {t(achievementTitleKey(nextUp))}
                </Text>
              </Text>
            </div>
          )}
        </section>

        {ACHIEVEMENT_CATEGORIES.map((category) => {
          const defs = SHOWN_ACHIEVEMENTS.filter((a) => a.category === category);
          const done = defs.filter((a) => isUnlocked(a, snapshot)).length;
          return (
            <section key={category} className="fluent-awards__section">
              <div className="fluent-awards__section-head" data-tone={category}>
                <span className="fluent-awards__section-dot" aria-hidden="true" />
                <Heading level={4} as="h2">
                  {t(`achievements.category.${category}`)}
                </Heading>
                <span className="fluent-awards__section-count">
                  {done}/{defs.length}
                </span>
              </div>
              <div className="fluent-awards__grid">
                {defs.map((a, i) => (
                  <Card key={a.id} achievement={a} snapshot={snapshot} index={i} />
                ))}
              </div>
            </section>
          );
        })}
      </Stack>
    </Container>
  );
}

/** The locked badge closest to unlocking — keeps a concrete goal on screen. */
function pickNextUp(s: ProgressSnapshot): AchievementDefinition | null {
  let best: AchievementDefinition | null = null;
  let bestProgress = -1;
  for (const a of SHOWN_ACHIEVEMENTS) {
    if (isUnlocked(a, s)) continue;
    const p = progressOf(a, s);
    if (p > bestProgress) {
      best = a;
      bestProgress = p;
    }
  }
  return best;
}

interface CardProps {
  achievement: AchievementDefinition;
  snapshot: ProgressSnapshot;
  index: number;
}

function Card({ achievement, snapshot, index }: CardProps) {
  const { t } = useTranslation();
  const unlocked = isUnlocked(achievement, snapshot);
  const progress = progressOf(achievement, snapshot);
  const current = Math.min(achievement.measure(snapshot), achievement.target);

  return (
    <div
      className="fluent-awards__card"
      data-tone={achievement.category}
      data-unlocked={unlocked || undefined}
      style={{ animationDelay: `${Math.min(index, 5) * 30}ms` }}
    >
      <div className="fluent-awards__coin" aria-hidden="true">
        <span className="fluent-awards__coin-icon">{achievement.icon}</span>
        {unlocked && <span className="fluent-awards__coin-check">✓</span>}
      </div>
      <Text weight="bold" size="sm" style={{ display: 'block' }}>
        {t(achievementTitleKey(achievement))}
      </Text>
      <Text tone="muted" size="xs" style={{ display: 'block', marginTop: 2 }}>
        {t(achievementDescriptionKey(achievement))}
      </Text>
      {!unlocked && !achievement.binary && (
        <div className="fluent-awards__card-progress">
          <ProgressBar
            value={progress}
            tone="xp"
            size="sm"
            label={t(achievementTitleKey(achievement))}
          />
          <span className="fluent-awards__card-count">
            {current}/{achievement.target}
          </span>
        </div>
      )}
    </div>
  );
}
